import { hashQueryKey } from "@tanstack/react-query";
import type { MarkRequired, StrictOmit } from "ts-essentials";
import { match } from "ts-pattern";
import type { OverrideProperties, RequireExactlyOne } from "type-fest";
import { millisecondsToNanoseconds, secondsToNanoseconds } from "~/lib/dates";
import { invariant } from "~/lib/invariant";
import { getOrInsert, getOrInsertComputed, sortedLastIndexBy } from "~/lib/std";
import type { ListRecordsRequest, RecordListResponse, Topic } from "~/lqs";
import { circumventPagination } from "~/lqs";
import type { Maybe, TimeRange } from "~/types";
import { assertNever } from "~/utils/assertions";
import { compareBigInts } from "~/utils/math";
import type { DataFilter, SampleFrequencyValue } from "../types";
import { SampleFrequency } from "../types";
import { NoRecordsError } from "./errors";
import { FetchLoopError, LoopDetector } from "./loop-detector";
import type {
  InitializableRecordTypeHandler,
  RecordTypeHandler,
} from "./record-types";
import { createRecordHandler, isInitializableHandler } from "./record-types";
import type {
  AllRecordsTopicSubscription,
  BoundedTopicSubscription,
  ExactRecordsRequest,
  ExactRecordsTopicSubscription,
  LimitedRecordsRequest,
  LimitedRecordsTopicSubscription,
  ListRecordsFn,
  PlayerRecord,
  RecordsRequest,
  RecordsRequestType,
  RecordsResponse,
  RecordType,
  TopicSubscription,
} from "./types";

type UsableSubscription<TRecordType extends RecordType> =
  | (BoundedTopicSubscription<TRecordType> & {
      topicStartTime: bigint;
      topicEndTime: bigint;
    })
  | ExactRecordsTopicSubscription<TRecordType>;

type CacheEntryIdentifier = Pick<
  RecordsRequest<RecordType>,
  "type" | "recordType" | "topicId"
> & {
  frequency?: number | null;
  queryDataFilter?: DataFilter | ReadonlyArray<DataFilter>;
  timestamps?: ReadonlyArray<bigint>;
};

// Exported for testing
export class RecordSequence<TRecordType extends RecordType>
  implements TimeRange
{
  readonly records: ReadonlyArray<PlayerRecord<TRecordType>>;

  readonly startTime: bigint;
  hasFirstTopicRecord: boolean;
  readonly endTime: bigint;
  hasLastTopicRecord: boolean;

  constructor({
    records,
    hasFirstTopicRecord = false,
    hasLastTopicRecord = false,
  }: {
    records: ReadonlyArray<PlayerRecord<TRecordType>>;
    hasFirstTopicRecord?: boolean;
    hasLastTopicRecord?: boolean;
  }) {
    invariant(records.length > 0, "Sequence must have at least one record");

    this.records = records;
    this.startTime = records.at(0)!.timestamp;
    this.hasFirstTopicRecord = hasFirstTopicRecord;
    this.endTime = records.at(-1)!.timestamp;
    this.hasLastTopicRecord = hasLastTopicRecord;
  }

  static from<TRecordType extends RecordType>(
    frequency: number | null,
    previousSequence: RecordSequence<TRecordType> | null,
    newSequence: RecordSequence<TRecordType> | null,
    nextSequence: RecordSequence<TRecordType> | null,
  ): RecordSequence<TRecordType> {
    let hasFirstTopicRecord = false;
    let hasLastTopicRecord = false;
    const frequencyBinMap = new Map<bigint, PlayerRecord<TRecordType>>();

    for (const sequence of [previousSequence, newSequence, nextSequence]) {
      if (sequence == null) {
        continue;
      }

      if (sequence.hasFirstTopicRecord) {
        hasFirstTopicRecord = true;
      }

      if (sequence.hasLastTopicRecord) {
        hasLastTopicRecord = true;
      }

      const isNewSequence = sequence === newSequence;

      for (const record of sequence.records) {
        const flooredTimestamp = floorTimestampForFrequency(
          record.timestamp,
          frequency as SampleFrequencyValue | null,
        );

        if (isNewSequence) {
          if (!frequencyBinMap.has(flooredTimestamp)) {
            frequencyBinMap.set(flooredTimestamp, record);
          }
        } else {
          frequencyBinMap.set(flooredTimestamp, record);
        }
      }
    }

    return new RecordSequence({
      records: Array.from(frequencyBinMap.values()).sort(comparePlayerRecords),
      hasFirstTopicRecord,
      hasLastTopicRecord,
    });
  }

  static compare(
    first: RecordSequence<RecordType>,
    second: RecordSequence<RecordType>,
  ): number {
    // For any two sequences in the same topic, one will always be entirely
    // prior to the other. This comparator is only intended to be used for
    // sorting sequences within the same topic.
    return first.startTime < second.startTime ? -1 : 1;
  }

  canHandleTimestamp(timestamp: bigint): boolean {
    return (
      (this.startTime <= timestamp && timestamp <= this.endTime) ||
      (this.hasFirstTopicRecord && timestamp < this.startTime) ||
      (this.hasLastTopicRecord && timestamp > this.endTime)
    );
  }

  calculateRemainingRecords(request: {
    timestamp: bigint;
    countBehind: number;
    countAhead: number;
  }): {
    behind: number;
    ahead: number;
  } {
    const availableRecords = this.#calculateAvailableRecords(request.timestamp);

    const initialBehind = Math.max(
      request.countBehind - availableRecords.behind,
      0,
    );
    const initialAhead = Math.max(
      request.countAhead - availableRecords.ahead,
      0,
    );

    let behind: number;
    let ahead: number;
    if (this.hasFirstTopicRecord && this.hasLastTopicRecord) {
      // Doesn't matter whether more needed to be fetched or not since all
      // available records are in this sequence.
      behind = 0;
      ahead = 0;
    } else if (this.hasFirstTopicRecord) {
      // No more records to fetch behind this sequence's start time but however
      // many were initially needed to be fetched behind should instead be
      // fetched ahead.
      behind = 0;
      ahead = Math.max(
        request.countAhead + initialBehind - availableRecords.ahead,
        0,
      );
    } else if (this.hasLastTopicRecord) {
      // No more records to fetch ahead of this sequence's end time but however
      // many were initially needed to be fetched ahead should instead be
      // fetched behind.
      behind = Math.max(
        request.countBehind + initialAhead - availableRecords.behind,
        0,
      );
      ahead = 0;
    } else {
      // Fetch records in both directions if necessary.
      behind = initialBehind;
      ahead = initialAhead;
    }

    return { behind, ahead };
  }

  getRecords(
    request: LimitedRecordsRequest<TRecordType>,
  ): Array<PlayerRecord<TRecordType>> | null {
    if (!this.canHandleTimestamp(request.timestamp)) {
      return null;
    }

    // `request.count` could be odd in which case the lower slice of records
    // will get one more than the upper slice.
    const countBehind = Math.ceil(request.count / 2);
    const countAhead = Math.floor(request.count / 2);

    const availableRecords = this.#calculateAvailableRecords(request.timestamp);

    if (this.records.length < request.count) {
      if (this.hasFirstTopicRecord && this.hasLastTopicRecord) {
        return this.records as Array<PlayerRecord<TRecordType>>;
      } else {
        return null;
      }
    } else if (availableRecords.behind < countBehind) {
      if (
        this.hasFirstTopicRecord &&
        availableRecords.ahead >=
          countAhead + (countBehind - availableRecords.behind)
      ) {
        return this.records.slice(0, request.count);
      } else {
        return null;
      }
    } else if (availableRecords.ahead < countAhead) {
      if (
        this.hasLastTopicRecord &&
        availableRecords.behind >=
          countBehind + (countAhead - availableRecords.ahead)
      ) {
        return this.records.slice(
          this.records.length - request.count,
          this.records.length,
        );
      } else {
        return null;
      }
    } else {
      return this.records.slice(
        availableRecords.index + 1 - countBehind,
        availableRecords.index + 1 + countAhead,
      );
    }
  }

  #calculateAvailableRecords(timestamp: bigint): {
    index: number;
    behind: number;
    ahead: number;
  } {
    const mostRecentRecordIndex = this.getMostRecentRecordIndex(timestamp);

    return {
      index: mostRecentRecordIndex,
      // Most recent record is included in the records available at or behind
      // the timestamp
      behind: mostRecentRecordIndex + 1,
      ahead: this.records.length - mostRecentRecordIndex - 1,
    };
  }

  getMostRecentRecordIndex(timestamp: bigint): number {
    // `sortedLastIndexBy` returns the last index where `timestamp` could be
    // inserted while maintaining sort order, meaning the index of the most
    // recent record (<= `timestamp`) is the previous index.
    //
    // This function could return -1 if `timestamp` is less than earliest
    // record in this sequence.
    return (
      sortedLastIndexBy(
        this.records,
        { timestamp } as PlayerRecord,
        "timestamp",
      ) - 1
    );
  }
}

async function processFetchedRecords<TRecordType extends RecordType>(
  handler: RecordTypeHandler<TRecordType>,
  response: RecordListResponse,
): Promise<PlayerRecordListResponse<TRecordType>> {
  return {
    ...response,
    data: await Promise.all(
      response.data.map(async (record): Promise<PlayerRecord<TRecordType>> => {
        const data = await handler.transform(record);

        return {
          timestamp: record.timestamp,
          topicId: record.topicId,
          context: record.context,
          data,
        };
      }),
    ),
  };
}

type TimestampFilters = RequireExactlyOne<{
  timestampGt: bigint;
  timestampGte: bigint;
}> &
  RequireExactlyOne<{
    timestampLt: bigint;
    timestampLte: bigint;
  }>;

type RequestParameters = Pick<ListRecordsRequest, "offset" | "sort"> &
  TimestampFilters;

type PlayerRecordListResponse<TRecordType extends RecordType> =
  OverrideProperties<
    RecordListResponse,
    { data: Array<PlayerRecord<TRecordType>> }
  >;

class RequestTracker<TRecordType extends RecordType> {
  lowerBoundSequenceEndTime: bigint | null;
  upperBoundSequenceStartTime: bigint | null;

  /**
   * When `true`, indicates it is known there are no more records in the topic
   * before the records that will be returned in this tracker's response (if
   * any). This is only set when this tracker's previous sibling's response was
   * processed at an earlier point. If this tracker's response also has no
   * records then it should set this flag on its next sibling, if one exists.
   */
  noPriorRecords = false;
  /**
   * `endTime` of the `RecordSequence` to which this tracker's response's
   * records should be appended.
   */
  previousSequenceEndTime: bigint | null = null;
  /**
   * When `true`, indicates it is known there are no more records in the topic
   * after the records that will be returned in this tracker's response (if
   * any). This is only set when this tracker's next sibling's response was
   * processed at an earlier point. If this tracker's response also has no
   * records then it should set this flag on its previous sibling, if one
   * exists.
   */
  noSubsequentRecords = false;
  /**
   * `startTime` of the `RecordSequence` to which this tracker's response's
   * records should be prepended.
   */
  nextSequenceStartTime: bigint | null = null;

  /**
   * Tracker whose eventual records, if any, are contiguous with this tracker's
   * eventual records, if any. Its records will have earlier timestamps than
   * this tracker's records.
   */
  previousSibling: RequestTracker<TRecordType> | null = null;
  /**
   * Tracker whose eventual records, if any, are contiguous with this tracker's
   * eventual records, if any. Its records will have later timestamps than this
   * tracker's records.
   */
  nextSibling: RequestTracker<TRecordType> | null = null;

  #response: PlayerRecordListResponse<TRecordType> | null = null;

  readonly #topicId: Topic["id"];
  readonly #frequency: number | null;
  readonly #limit: number;
  readonly #requestParameters: RequestParameters;

  readonly #handler: RecordTypeHandler<TRecordType>;

  readonly #listRecords: ListRecordsFn;
  readonly #controller = new AbortController();

  constructor({
    topicId,
    frequency,
    limit,
    handler,
    requestParameters,
    listRecords,
    lowerBoundSequenceEndTime,
    upperBoundSequenceStartTime,
  }: {
    topicId: Topic["id"];
    frequency: number | null;
    limit: number;
    handler: RecordTypeHandler<TRecordType>;
    requestParameters: RequestParameters;
    lowerBoundSequenceEndTime: bigint | null;
    upperBoundSequenceStartTime: bigint | null;
    listRecords: ListRecordsFn;
  }) {
    this.lowerBoundSequenceEndTime = lowerBoundSequenceEndTime;
    this.upperBoundSequenceStartTime = upperBoundSequenceStartTime;

    this.#topicId = topicId;
    this.#frequency = frequency;
    this.#limit = limit;
    this.#requestParameters = requestParameters;

    this.#handler = handler;

    this.#listRecords = listRecords;
  }

  getTimeRange(): TimeRange {
    return toClosedInterval(this.#requestParameters);
  }

  getPreviousSequence(
    sequences: ReadonlyArray<RecordSequence<TRecordType>>,
  ): RecordSequence<TRecordType> | null {
    this.#requireResolvedResponse();

    let previousSequence: RecordSequence<TRecordType> | null = null;
    if (this.previousSequenceEndTime !== null) {
      // At some earlier point, this tracker's previous sibling's request
      // finished with at least one record. Since this tracker's records are
      // guaranteed to be adjacent to that sibling's records, this tracker can
      // be merged directly with its sibling's sequence.
      previousSequence = sequences.find(
        (sequence) => sequence.endTime === this.previousSequenceEndTime,
      )!;
    } else if (
      this.exhaustedPaginationInDirection("desc") &&
      this.lowerBoundSequenceEndTime != null
    ) {
      // No previous sibling finished prior to this point; however, there are
      // no more records to fetch in the descending direction which means these
      // records can be merged into the original sequence which acted as a lower
      // bound for filtering, if one exists.
      previousSequence = sequences.find(
        (sequence) => sequence.endTime === this.lowerBoundSequenceEndTime,
      )!;
    }

    return previousSequence;
  }

  getNextSequence(
    sequences: ReadonlyArray<RecordSequence<TRecordType>>,
  ): RecordSequence<TRecordType> | null {
    this.#requireResolvedResponse();

    let nextSequence: RecordSequence<TRecordType> | null = null;
    if (this.nextSequenceStartTime !== null) {
      // At some earlier point, this tracker's next sibling's request finished
      // with at least one record. Since this tracker's records are guaranteed
      // to be adjacent to that sibling's records, this tracker can be merged
      // directly with its sibling's sequence.
      nextSequence = sequences.find(
        (sequence) => sequence.startTime === this.nextSequenceStartTime,
      )!;
    } else if (
      this.exhaustedPaginationInDirection("asc") &&
      this.upperBoundSequenceStartTime != null
    ) {
      // No subsequent sibling finished prior to this point; however, there are
      // no more records to fetch in the ascending direction which means these
      // records can be merged into the original sequence which acted as an
      // upper bound for filtering, if one exists.
      nextSequence = sequences.find(
        (sequence) => sequence.startTime === this.upperBoundSequenceStartTime,
      )!;
    }

    return nextSequence;
  }

  /**
   * Returns `true` if this tracker's response could possibly contain the
   * topic's first record according to `topicStartTime`. Always `false` if
   * this tracker's request is sorted by ascending timestamp
   */
  couldHaveFirstRecord(topicStartTime: bigint): boolean {
    return (
      this.#requestParameters.timestampGte === topicStartTime &&
      this.#requestParameters.sort === "desc"
    );
  }

  /**
   * Returns `true` if this tracker's response could possibly contain the
   * topic's last record according to `topicEndTime`. Always `false` if this
   * tracker's request is sorted by descending timestamp
   */
  couldHaveLastRecord(topicEndTime: bigint): boolean {
    return (
      this.#requestParameters.timestampLte === topicEndTime &&
      this.#requestParameters.sort === "asc"
    );
  }

  /**
   * Returns `true` if this tracker's response indicates no more records would
   * be returned if the same filters were used with a larger offset.
   */
  exhaustedPaginationInDirection(direction: "asc" | "desc"): boolean {
    const response = this.#requireResolvedResponse();

    return response.sort === direction && response.data.length < response.limit;
  }

  #requireResolvedResponse(): PlayerRecordListResponse<TRecordType> {
    invariant(this.#response !== null, "Request isn't resolved");

    return this.#response;
  }

  abort(): void {
    this.#controller.abort();
  }

  get aborted(): boolean {
    return this.#controller.signal.aborted;
  }

  async fetch(): Promise<PlayerRecordListResponse<TRecordType>> {
    const baseResponse = await this.#listRecords(
      {
        topicId: this.#topicId,
        frequency: this.#frequency,
        includeCount: false,
        order: "timestamp",
        limit: this.#limit,
        ...this.#requestParameters,
        ...this.#handler.getRequestParams?.(),
      },
      { signal: this.#controller.signal },
    );

    enforceResponseSortCorrectness(baseResponse);

    this.#response = await processFetchedRecords(this.#handler, baseResponse);

    return this.#response;
  }
}

type EntryRequest<
  TRequestType extends RecordsRequestType,
  TRecordType extends RecordType,
> = Extract<RecordsRequest<TRecordType>, { type: TRequestType }>;

type EntrySubscription<
  TRequestType extends RecordsRequestType,
  TRecordType extends RecordType,
> = Extract<TopicSubscription<TRecordType>, { type: TRequestType }>;

abstract class AbstractCacheEntry<
  TRequestType extends RecordsRequestType,
  TRecordType extends RecordType,
> {
  readonly type: TRequestType;
  readonly recordType: TRecordType;
  readonly topicId: Topic["id"];

  protected readonly listRecords: ListRecordsFn;

  protected readonly handler: RecordTypeHandler<TRecordType>;

  protected readonly subscribers = new Set<
    EntrySubscription<TRequestType, TRecordType>
  >();

  #scheduledFetchesTimeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly #evictCacheEntry: (cacheKey: string) => void;
  #evictionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor({
    type,
    recordType,
    topicId,
    listRecords,
    evictCacheEntry,
  }: {
    type: TRequestType;
    recordType: TRecordType;
    topicId: Topic["id"];
    listRecords: ListRecordsFn;
    evictCacheEntry: (cacheKey: string) => void;
  }) {
    this.type = type;
    this.recordType = recordType;
    this.topicId = topicId;

    this.listRecords = listRecords;

    this.handler = createRecordHandler(recordType);

    this.#evictCacheEntry = evictCacheEntry;
  }

  abstract getRecords(
    request: EntryRequest<TRequestType, TRecordType>,
  ): RecordsResponse<TRecordType>;

  protected abstract onError(error: unknown): void;

  protected abstract hasError(): boolean;

  abstract clearError(): void;

  subscribe(
    subscription: EntrySubscription<TRequestType, TRecordType>,
  ): () => void {
    this.cancelEvictionTimeout();

    this.subscribers.add(subscription);

    this.onSubscribe(subscription);

    this.#beginFetching(subscription);

    return () => {
      this.subscribers.delete(subscription);

      this.onUnsubscribe(subscription);

      if (this.subscribers.size === 0) {
        this.scheduleEviction();
      }
    };
  }

  protected onSubscribe(
    subscription: EntrySubscription<TRequestType, TRecordType>,
  ): void {
    // Default implementation
  }

  protected onUnsubscribe(
    subscription: EntrySubscription<TRequestType, TRecordType>,
  ): void {
    // Default implementation
  }

  protected abstract fetchRecords(
    subscription: EntrySubscription<TRequestType, TRecordType>,
  ): void;

  #beginFetching(
    subscription: EntrySubscription<TRequestType, TRecordType>,
  ): void {
    if (this.hasError()) {
      return;
    }

    if (
      isInitializableHandler(this.handler) &&
      this.handler.getInitializationStatus() !== "fulfilled"
    ) {
      if (this.handler.getInitializationStatus() === "pending") {
        return;
      }

      this.handler
        .initialize({
          topicId: subscription.topicId,
          listRecords: this.listRecords,
        })
        .then(() => {
          this.scheduleFetches();
        })
        .catch((reason) => {
          // Reset handler now so initialization can be retried when user
          // clears error.
          (
            this.handler as InitializableRecordTypeHandler<TRecordType>
          ).resetInitialization();

          this.onError(reason);
        });

      return;
    }

    try {
      this.fetchRecords(subscription);
    } catch (e) {
      this.onError(e);
    }
  }

  protected scheduleFetches(): void {
    if (this.hasError()) {
      return;
    }

    if (this.#scheduledFetchesTimeoutId !== null) {
      return;
    }

    this.#scheduledFetchesTimeoutId = setTimeout(() => {
      this.subscribers.forEach((subscriber) => {
        this.#beginFetching(subscriber);
      });

      this.#scheduledFetchesTimeoutId = null;
    }, 0);
  }

  protected cancelScheduledFetches(): void {
    if (this.#scheduledFetchesTimeoutId === null) {
      return;
    }

    clearTimeout(this.#scheduledFetchesTimeoutId);
    this.#scheduledFetchesTimeoutId = null;
  }

  protected abstract onEvict(): void;

  protected disposeOfRecords(
    records:
      | PlayerRecord<TRecordType>
      | ReadonlyArray<PlayerRecord<TRecordType>>,
  ): void {
    if (!Array.isArray(records)) {
      records = [records as PlayerRecord<TRecordType>];
    }

    if (this.handler.dispose != null) {
      for (const record of records) {
        this.handler.dispose(record);
      }
    }
  }

  protected scheduleEviction(): void {
    if (this.subscribers.size > 0) {
      return;
    }

    if (this.#evictionTimeoutId !== null) {
      return;
    }

    this.#evictionTimeoutId = setTimeout(() => {
      this.#evictionTimeoutId = null;

      this.onEvict();

      this.#evictCacheEntry(createCacheKey(this));
    }, 15_000);
  }

  protected cancelEvictionTimeout(): void {
    if (this.#evictionTimeoutId === null) {
      return;
    }

    clearTimeout(this.#evictionTimeoutId);

    this.#evictionTimeoutId = null;
  }

  protected notifySubscribers(): void {
    this.subscribers.forEach((subscriber) => subscriber.notify());
  }
}

class AllRecordsCacheEntry<
  TRecordType extends RecordType,
> extends AbstractCacheEntry<"all", TRecordType> {
  readonly frequency: number | null;
  readonly queryDataFilter: ReadonlyArray<DataFilter>;

  topicStartTime: bigint;
  topicEndTime: bigint;

  readonly #controller = new AbortController();

  #result: RecordsResponse<TRecordType> | null = null;

  constructor({
    type,
    recordType,
    topicId,
    frequency,
    queryDataFilter,
    topicStartTime,
    topicEndTime,
    records,
    listRecords,
    evictCacheEntry,
  }: {
    type: "all";
    recordType: TRecordType;
    topicId: Topic["id"];
    frequency: number | null;
    queryDataFilter?: DataFilter | ReadonlyArray<DataFilter>;
    topicStartTime: bigint;
    topicEndTime: bigint;
    records?: Array<Array<PlayerRecord<TRecordType>>>;
    listRecords: ListRecordsFn;
    evictCacheEntry: (cacheKey: string) => void;
  }) {
    super({
      type,
      recordType,
      topicId,
      listRecords,
      evictCacheEntry,
    });

    this.frequency = frequency;
    this.queryDataFilter = normalizeDataFilers(queryDataFilter);

    this.topicStartTime = topicStartTime;
    this.topicEndTime = topicEndTime;

    if (records != null) {
      invariant(
        records.length === 1 &&
          records[0].at(0)?.timestamp === topicStartTime &&
          records[0].at(-1)?.timestamp === topicEndTime,
        "Expected a single array of records spanning entire topic",
      );

      this.#result = {
        status: "fulfilled",
        value: records[0],
      };
    }
  }

  getRecords(): RecordsResponse<TRecordType> {
    return this.#result ?? { status: "pending" };
  }

  protected onError(error: unknown): void {
    this.#result = {
      status: "rejected",
      reason: error,
    };

    this.cancelScheduledFetches();
    this.notifySubscribers();
  }

  protected hasError(): boolean {
    return this.#result?.status === "rejected";
  }

  clearError(): void {
    invariant(this.#result?.status === "rejected", "No error to clear");

    this.#result = null;

    this.scheduleFetches();
    this.notifySubscribers();
  }

  protected fetchRecords(
    subscription: AllRecordsTopicSubscription<TRecordType>,
  ): void {
    if (this.#result != null) {
      return;
    }

    this.#result = { status: "pending" };

    const {
      topicId,
      frequency,
      queryDataFilter: initialQueryDataFilter,
      limit,
    } = subscription;

    const queryDataFilter = normalizeDataFilers(initialQueryDataFilter);

    const baseRequest: ListRecordsRequest = {
      topicId,
      sort: "asc",
      order: "timestamp",
      frequency,
      queryDataFilter:
        queryDataFilter.length === 0 ? null : JSON.stringify(queryDataFilter),
      ...this.handler.getRequestParams?.(),
    };

    circumventPagination(
      async (request, init): Promise<PlayerRecordListResponse<TRecordType>> => {
        const response = await this.listRecords(request, init);

        return processFetchedRecords(this.handler, response);
      },
      limit,
      baseRequest,
      { signal: this.#controller.signal },
    )
      .then((response) => {
        const frequencyBinMap = new Map<bigint, PlayerRecord<TRecordType>>();

        for (const record of response.data) {
          const flooredTimestamp = floorTimestampForFrequency(
            record.timestamp,
            frequency as SampleFrequencyValue | null,
          );

          if (frequencyBinMap.has(flooredTimestamp)) {
            continue;
          }

          frequencyBinMap.set(flooredTimestamp, record);
        }

        if (frequencyBinMap.size === 0) {
          throw new NoRecordsError();
        }

        this.#result = {
          status: "fulfilled",
          value: Array.from(frequencyBinMap.values()).sort(
            comparePlayerRecords,
          ),
        };

        this.topicStartTime = this.#result.value.at(0)!.timestamp;
        this.topicEndTime = this.#result.value.at(-1)!.timestamp;
      })
      .catch((reason) => {
        this.#result = {
          status: "rejected",
          reason,
        };

        this.cancelScheduledFetches();
      })
      .finally(() => {
        this.notifySubscribers();
      });
  }

  protected onEvict(): void {
    this.#controller.abort();

    if (this.#result?.status !== "fulfilled") {
      return;
    }

    this.disposeOfRecords(this.#result.value);
  }
}

class LimitedRecordsCacheEntry<
  TRecordType extends RecordType,
> extends AbstractCacheEntry<"limited", TRecordType> {
  readonly frequency: number | null;

  topicStartTime: bigint;
  topicEndTime: bigint;

  #sequences: Array<RecordSequence<TRecordType>>;

  readonly #loopDetector = new LoopDetector();

  readonly #trackers = new Set<RequestTracker<TRecordType>>();

  #error: unknown = null;

  constructor({
    type,
    recordType,
    topicId,
    frequency,
    topicStartTime,
    topicEndTime,
    records = [],
    listRecords,
    evictCacheEntry,
  }: {
    type: "limited";
    recordType: TRecordType;
    topicId: Topic["id"];
    frequency: number | null;
    topicStartTime: bigint;
    topicEndTime: bigint;
    records?: Array<Array<PlayerRecord<TRecordType>>>;
    listRecords: ListRecordsFn;
    evictCacheEntry: (cacheKey: string) => void;
  }) {
    super({
      type,
      recordType,
      topicId,
      listRecords,
      evictCacheEntry,
    });

    this.frequency = frequency;

    this.topicStartTime = topicStartTime;
    this.topicEndTime = topicEndTime;

    this.#sequences = records.map(
      (subRecords) =>
        new RecordSequence({
          records: subRecords,
          hasFirstTopicRecord: subRecords.at(0)?.timestamp === topicStartTime,
          hasLastTopicRecord: subRecords.at(-1)?.timestamp === topicEndTime,
        }),
    );
  }

  getRecords(
    request: LimitedRecordsRequest<TRecordType>,
  ): RecordsResponse<TRecordType> {
    if (this.#error) {
      return {
        status: "rejected",
        reason: this.#error,
      };
    }

    let records: Array<PlayerRecord<TRecordType>> | null = null;
    for (const sequence of this.#sequences) {
      records = sequence.getRecords(request);

      if (records !== null) {
        break;
      }
    }

    if (records === null) {
      return {
        status: "pending",
      };
    } else {
      return {
        status: "fulfilled",
        value: records,
      };
    }
  }

  protected onError(error: unknown): void {
    this.#error = error;

    this.cancelScheduledFetches();
    this.notifySubscribers();
  }

  protected hasError(): boolean {
    return Boolean(this.#error);
  }

  clearError(): void {
    // Reset the loop detector if it's the reason the store is in an error
    // state. Otherwise, it'd likely immediately put the store back into an
    // error state.
    if (this.#error instanceof FetchLoopError) {
      this.#loopDetector.reset();
    }

    this.#error = null;

    this.scheduleFetches();
    this.notifySubscribers();
  }

  protected override onSubscribe(
    subscription: LimitedRecordsTopicSubscription<TRecordType>,
  ): void {
    const minimumCacheCount = calculateMinimumCacheCount(subscription);

    invariant(
      subscription.cacheCount == null ||
        subscription.cacheCount >= minimumCacheCount,
      "Subscription's cache count is not large enough to prevent thrashing.\n" +
        "This is a bug in Studio.\n" +
        `Cache count: ${subscription.cacheCount}. Minimum: ${minimumCacheCount}`,
    );

    this.#handleEvictions();
    this.#loopDetector.addSubscription(subscription);
  }

  protected override onUnsubscribe(
    subscription: LimitedRecordsTopicSubscription<TRecordType>,
  ): void {
    this.#loopDetector.removeSubscription(subscription);
  }

  fetchRecords(
    subscription: LimitedRecordsTopicSubscription<TRecordType>,
  ): void {
    const { frequency, count, timestamp, prefetchBehind, prefetchAhead } =
      subscription;

    const { pastSequence, containingSequence, futureSequence } =
      findBoundingSequences({
        frequency,
        timestamp,
        sequences: this.#sequences,
      });

    const { descendingFilters, ascendingFilters, alignedWithBin } =
      calculateTimestampFilters({
        frequency,
        timestamp,
        pastSequence,
        containingSequence,
        futureSequence,
        topicStartTime: this.topicStartTime,
        topicEndTime: this.topicEndTime,
      });

    const { descendingCount, ascendingCount } = calculateDirectionalCounts({
      count,
      timestamp,
      prefetchBehind,
      prefetchAhead,
      topicStartTime: this.topicStartTime,
      topicEndTime: this.topicEndTime,
      containingSequence,
    });

    let head: RequestTracker<TRecordType> | null = null;
    let tail: RequestTracker<TRecordType> | null = null;

    const createTracker = (
      requestParameters: RequestParameters,
      lowerBoundSequence: Maybe<RecordSequence<TRecordType>>,
      upperBoundSequence: Maybe<RecordSequence<TRecordType>>,
      limit = subscription.limit,
    ): RequestTracker<TRecordType> => {
      const tracker = new RequestTracker({
        topicId: subscription.topicId,
        frequency: subscription.frequency,
        limit,
        handler: this.handler,
        requestParameters,
        listRecords: this.listRecords,
        lowerBoundSequenceEndTime: lowerBoundSequence?.endTime ?? null,
        upperBoundSequenceStartTime: upperBoundSequence?.startTime ?? null,
      });

      tracker
        .fetch()
        .then((response) => this.#handleResponseData(tracker, response))
        .catch((error) => this.#handleResponseError(tracker, error));

      if (head === null || tail === null) {
        head = tracker;
        tail = tracker;
      } else {
        tracker.previousSibling = tail;
        tail.nextSibling = tracker;
        tail = tracker;
      }

      return tracker;
    };

    if (
      descendingCount > 0 &&
      checkAreFiltersValid(descendingFilters) &&
      checkNoOverlappingRequests(descendingFilters, this.#getTrackersAsArray())
    ) {
      // Will throw if detector thinks store is in a fetching loop
      this.#loopDetector.recordIteration(subscription, "desc");

      const descendingRequestsCount = Math.ceil(
        descendingCount / subscription.limit,
      );

      // The final array of requests needs to be sorted by ascending timestamp
      // of the records each is expected to return. Since requests for past
      // records are sorted by descending timestamp the requests need to be
      // pushed in reverse order. The fetched records will also need to be
      // reversed too.
      for (
        let requestIndex = descendingRequestsCount - 1;
        requestIndex >= 0;
        requestIndex--
      ) {
        const tracker = createTracker(
          {
            ...descendingFilters,
            sort: "desc",
            offset: requestIndex * subscription.limit,
          },
          pastSequence,
          containingSequence,
        );

        if (requestIndex === 0 && containingSequence != null) {
          tracker.nextSequenceStartTime = containingSequence.startTime;
        }
      }
    }

    // If the descending and ascending requests are separated by an existing
    // sequence which contains the timestamp, their records will not be
    // contiguous, meaning they shouldn't be treated as siblings. Instead,
    // all descending requests are treated as their own set of siblings and
    // the ascending requests will be treated as their own too.
    if (head !== null && containingSequence != null) {
      this.#trackers.add(head);

      head = null;
      tail = null;
    }

    if (
      (ascendingCount > 0 || alignedWithBin) &&
      checkAreFiltersValid(ascendingFilters) &&
      checkNoOverlappingRequests(ascendingFilters, this.#getTrackersAsArray())
    ) {
      // Will throw if detector thinks store is in a fetching loop
      this.#loopDetector.recordIteration(subscription, "asc");

      const ascendingRequestsCount = Math.ceil(
        ascendingCount / subscription.limit,
      );

      let requestIndex = 0;

      if (alignedWithBin) {
        createTracker(
          {
            ...ascendingFilters,
            sort: "asc",
            offset: 0,
          },
          null,
          futureSequence,
          subscription.limit + 1,
        );

        requestIndex++;
      }

      const offsetAdjustment = alignedWithBin ? 1 : 0;

      for (; requestIndex < ascendingRequestsCount; requestIndex++) {
        const tracker = createTracker(
          {
            ...ascendingFilters,
            sort: "asc",
            offset: requestIndex * subscription.limit + offsetAdjustment,
          },
          containingSequence,
          futureSequence,
        );

        if (requestIndex === 0 && containingSequence != null) {
          tracker.previousSequenceEndTime = containingSequence.endTime;
        }
      }
    }

    if (head !== null) {
      this.#trackers.add(head);
    }
  }

  protected onEvict(): void {
    this.#trackers.forEach((tracker) => tracker.abort());

    for (const sequence of this.#sequences) {
      this.disposeOfRecords(sequence.records);
    }
  }

  #handleResponseData = (
    tracker: RequestTracker<TRecordType>,
    response: PlayerRecordListResponse<TRecordType>,
  ): void => {
    // Request was aborted earlier for some reason. Tracker should already
    // have been removed and there's nothing new to notify subscribers of.
    if (tracker.aborted) {
      return;
    }

    const previousSequence = tracker.getPreviousSequence(this.#sequences);
    if (previousSequence !== null) {
      removeArrayElement(this.#sequences, previousSequence);
    }

    const nextSequence = tracker.getNextSequence(this.#sequences);
    if (nextSequence !== null) {
      removeArrayElement(this.#sequences, nextSequence);
    }

    const { previousSibling, nextSibling } = tracker;

    let newSequence: RecordSequence<TRecordType> | null = null;
    if (response.data.length > 0) {
      // Records <= the playback time get fetched in descending order but need
      // to be stored in ascending order.
      const records =
        response.sort === "desc" ? [...response.data].reverse() : response.data;

      newSequence = new RecordSequence({
        records,
        hasFirstTopicRecord: records.at(0)!.timestamp === this.topicStartTime,
        hasLastTopicRecord: records.at(-1)!.timestamp === this.topicEndTime,
      });

      // It's possible the first or last record in a topic (whose timestamps
      // are used for a topic's `startTime` and `endTime`) could have been
      // deleted between the topic being fetched and the store attempting
      // to fetch those records. This would make the topic's `startTime` and/or
      // `endTime` different from what was originally stored on this entry.
      // This entry must be able to recognize this and update the stored topic
      // endpoints else it might get caught in a loop trying to fetch records
      // that no longer exist.
      if (
        (tracker.couldHaveFirstRecord(this.topicStartTime) &&
          tracker.exhaustedPaginationInDirection("desc")) ||
        tracker.noPriorRecords
      ) {
        newSequence.hasFirstTopicRecord = true;
        this.topicStartTime = newSequence.startTime;
      }

      if (
        (tracker.couldHaveLastRecord(this.topicEndTime) &&
          tracker.exhaustedPaginationInDirection("asc")) ||
        tracker.noSubsequentRecords
      ) {
        newSequence.hasLastTopicRecord = true;
        this.topicEndTime = newSequence.endTime;
      }

      // If this tracker had siblings, its new sequence's start or end time
      // should be stored directly on them so once the siblings' records are
      // fetched they can be merged in directly. This is possible since:
      //   1. All trackers were initially sorted such that the resulting
      //      sequences could conceptually all be concatenated and still be
      //      sorted correctly.
      //   2. Offsets for all trackers fetching in the same sort direction
      //      will produce contiguous records.
      //   3. Timestamp filters between trackers fetching in opposite directions
      //      ensure the first record <= the playback time and the first record
      //      > the playback time are sequential.
      if (previousSibling !== null) {
        previousSibling.nextSequenceStartTime = newSequence.startTime;
      }

      if (nextSibling !== null) {
        nextSibling.previousSequenceEndTime = newSequence.endTime;
      }
    } else {
      // Since the response had no records there isn't a new sequence whose
      // bounds should be set on its siblings; however, if some sequences exist
      // that would've been merged with the new sequence, those can be set on
      // the siblings so they can eventually merge with those. Without these
      // checks then certain gaps between sequences may never be merged.
      if (nextSequence !== null && previousSibling !== null) {
        previousSibling.nextSequenceStartTime = nextSequence.startTime;
      }

      if (previousSequence !== null && nextSibling !== null) {
        nextSibling.previousSequenceEndTime = previousSequence.endTime;
      }

      // See comment in if-block above explaining why topic start and end times
      // might need updated. In the below cases, this tracker's response doesn't
      // have the first or last record since it had 0 records but its sort
      // direction and timestamp filters indicate it could've.
      if (tracker.couldHaveFirstRecord(this.topicStartTime)) {
        // Does `nextSequence !== null` imply `nextSibling === null`?
        if (nextSequence !== null && nextSibling === null) {
          // This tracker would've merged with the next sequence if it had
          // records. Since it was expected to possibly contain the topic's
          // first record but 0 were found, the next sequence should be treated
          // as containing the first record.
          nextSequence.hasFirstTopicRecord = true;
          this.topicStartTime = nextSequence.startTime;
        } else if (nextSibling !== null) {
          // While it's not guaranteed the topic's original first record has
          // been deleted - this branch could be reached if this tracker had
          // too large of an offset and its next sibling contains the first
          // record - it's certain there won't be any records prior to its next
          // siblings'. However, this check is important when deleted records
          // make it such that the topic's new first record will be returned
          // in an ascending response which, typically, would not be expected
          // to contain the first record. By marking the sibling like this, any
          // adjustments to the topic's start or end times can be determined
          // when the sibling resolves.
          nextSibling.noPriorRecords = true;
        }
      }

      if (tracker.couldHaveLastRecord(this.topicEndTime)) {
        if (previousSequence !== null && previousSibling === null) {
          previousSequence.hasLastTopicRecord = true;
          this.topicEndTime = previousSequence.endTime;
        } else if (previousSibling !== null) {
          previousSibling.noSubsequentRecords = true;
        }
      }
    }

    if (tracker.exhaustedPaginationInDirection("desc")) {
      // Cancel any ongoing requests in the descending direction as it's now
      // known they won't return any records.
      let obsoleteTracker = previousSibling;
      while (obsoleteTracker !== null) {
        obsoleteTracker.abort();

        if (obsoleteTracker.previousSibling === null) {
          // This tracker is the head of its chain of siblings which means
          // it's a member of the set of trackers. It needs to be removed so
          // it doesn't end up blocking future requests.
          this.#trackers.delete(obsoleteTracker);
        }

        const prev = obsoleteTracker.previousSibling;

        obsoleteTracker.nextSibling = null;
        obsoleteTracker.previousSibling = null;

        obsoleteTracker = prev;
      }

      tracker.previousSibling = null;
    }

    if (tracker.exhaustedPaginationInDirection("asc")) {
      // Cancel any ongoing requests in the ascending direction as it's now
      // known they won't return any records.
      let obsoleteTracker = nextSibling;
      while (obsoleteTracker !== null) {
        obsoleteTracker.abort();

        const next = obsoleteTracker.nextSibling;

        obsoleteTracker.previousSibling = null;
        obsoleteTracker.nextSibling = null;

        obsoleteTracker = next;
      }

      tracker.nextSibling = null;
    }

    if (
      previousSequence !== null ||
      newSequence !== null ||
      nextSequence !== null
    ) {
      this.#sequences.push(
        RecordSequence.from(
          this.frequency,
          previousSequence,
          newSequence,
          nextSequence,
        ),
      );
      // Sort the sequences in place since the merged sequence above was just
      // pushed onto the end
      this.#sequences.sort(RecordSequence.compare);
    }

    this.#removeTracker(tracker);
    this.#handleEvictions();
    this.scheduleFetches();
    this.notifySubscribers();
  };

  #handleResponseError = (
    tracker: RequestTracker<TRecordType>,
    error: unknown,
  ): void => {
    // Request was aborted earlier for some reason. Tracker should already
    // have been removed and there's nothing new to notify subscribers of.
    if (tracker.aborted) {
      return;
    }

    this.#removeTracker(tracker);

    this.#error = error;

    this.cancelScheduledFetches();
    this.notifySubscribers();
  };

  #removeTracker(tracker: RequestTracker<TRecordType>): void {
    const { previousSibling, nextSibling } = tracker;

    if (previousSibling !== null) {
      tracker.previousSibling = null;
      previousSibling.nextSibling = null;
    } else {
      this.#trackers.delete(tracker);
    }

    if (nextSibling !== null) {
      tracker.nextSibling = null;
      nextSibling.previousSibling = null;

      this.#trackers.add(nextSibling);
    }
  }

  #handleEvictions(): void {
    if (this.subscribers.size === 0) {
      return;
    }

    const evictionStatuses = calculateEvictionStatuses(
      this.#sequences,
      this.subscribers,
    );

    const newSequences = new Array<RecordSequence<TRecordType>>();

    const trackers = this.#getTrackersAsArray();

    for (let i = 0; i < this.#sequences.length; i++) {
      const sequence = this.#sequences[i];
      const status = evictionStatuses[i];

      switch (status.action) {
        case "keep": {
          newSequences.push(sequence);

          break;
        }
        case "remove": {
          this.disposeOfRecords(sequence.records);

          for (const tracker of trackers) {
            if (tracker.lowerBoundSequenceEndTime === sequence.endTime) {
              tracker.lowerBoundSequenceEndTime = null;
            }

            if (tracker.upperBoundSequenceStartTime === sequence.startTime) {
              tracker.upperBoundSequenceStartTime = null;
            }

            if (tracker.previousSequenceEndTime === sequence.endTime) {
              tracker.previousSequenceEndTime = null;
            }

            if (tracker.nextSequenceStartTime === sequence.startTime) {
              tracker.nextSequenceStartTime = null;
            }
          }

          break;
        }
        case "slice": {
          const subSequences = new Array<RecordSequence<TRecordType>>();

          let startTimeMaintained = false;
          let endTimeMaintained = false;

          for (const slice of status.slices) {
            const subRecords = new Array<PlayerRecord<TRecordType>>();
            for (let j = 0; j < sequence.records.length; j++) {
              if (slice[0] <= j && j < slice[1]) {
                subRecords.push(sequence.records[j]);
              } else {
                this.disposeOfRecords(sequence.records[j]);
              }
            }

            const subSequence = new RecordSequence({ records: subRecords });

            subSequence.hasFirstTopicRecord =
              sequence.hasFirstTopicRecord &&
              subSequence.startTime === sequence.startTime;

            subSequence.hasLastTopicRecord =
              sequence.hasLastTopicRecord &&
              subSequence.endTime === sequence.endTime;

            if (subSequence.startTime === sequence.startTime) {
              startTimeMaintained = true;
            }

            if (subSequence.endTime === sequence.endTime) {
              endTimeMaintained = true;
            }

            subSequences.push(subSequence);
          }

          if (!startTimeMaintained || !endTimeMaintained) {
            for (const tracker of trackers) {
              if (!endTimeMaintained) {
                if (tracker.lowerBoundSequenceEndTime === sequence.endTime) {
                  tracker.lowerBoundSequenceEndTime = null;
                }

                if (tracker.previousSequenceEndTime === sequence.endTime) {
                  tracker.previousSequenceEndTime = null;
                }
              }

              if (!startTimeMaintained) {
                if (
                  tracker.upperBoundSequenceStartTime === sequence.startTime
                ) {
                  tracker.upperBoundSequenceStartTime = null;
                }

                if (tracker.nextSequenceStartTime === sequence.startTime) {
                  tracker.nextSequenceStartTime = null;
                }
              }
            }
          }

          newSequences.push(...subSequences);

          break;
        }
        default: {
          assertNever(status);
        }
      }
    }

    this.#sequences = newSequences;
    this.#sequences.sort(RecordSequence.compare);
  }

  #getTrackersAsArray(): ReadonlyArray<RequestTracker<TRecordType>> {
    const trackersArray = new Array<RequestTracker<TRecordType>>();

    this.#trackers.forEach((tracker) => {
      let currentTracker: RequestTracker<TRecordType> | null = tracker;
      while (currentTracker !== null) {
        trackersArray.push(currentTracker);

        currentTracker = currentTracker.nextSibling;
      }
    });

    return trackersArray;
  }
}

class ExactRecordsCacheEntry<
  TRecordType extends RecordType,
> extends AbstractCacheEntry<"exact", TRecordType> {
  readonly timestamps: ReadonlyArray<bigint>;

  readonly #controller = new AbortController();

  #result: RecordsResponse<TRecordType> | null = null;

  constructor({
    type,
    recordType,
    topicId,
    timestamps,
    records,
    listRecords,
    evictCacheEntry,
  }: {
    type: "exact";
    recordType: TRecordType;
    topicId: Topic["id"];
    timestamps: ReadonlyArray<bigint>;
    records?: Array<PlayerRecord<TRecordType>>;
    listRecords: ListRecordsFn;
    evictCacheEntry: (cacheKey: string) => void;
  }) {
    super({
      type,
      recordType,
      topicId,
      listRecords,
      evictCacheEntry,
    });

    this.timestamps = timestamps;

    if (records != null) {
      invariant(
        records.length > 0 &&
          records.every((record) => timestamps.includes(record.timestamp)),
        "Expected non-empty list of records whose timestamps all correspond to timestamp list",
      );

      this.#result = {
        status: "fulfilled",
        value: records,
      };
    }
  }

  getRecords(): RecordsResponse<TRecordType> {
    return this.#result ?? { status: "pending" };
  }

  protected onError(error: unknown): void {
    this.#result = {
      status: "rejected",
      reason: error,
    };

    this.cancelScheduledFetches();
    this.notifySubscribers();
  }

  protected hasError(): boolean {
    return this.#result?.status === "rejected";
  }

  clearError(): void {
    invariant(this.#result?.status === "rejected", "No error to clear");

    this.#result = null;

    this.scheduleFetches();
    this.notifySubscribers();
  }

  protected fetchRecords(
    subscription: ExactRecordsTopicSubscription<TRecordType>,
  ): void {
    if (this.#result != null) {
      return;
    }

    this.#result = { status: "pending" };

    const { topicId, timestamps } = subscription;

    const baseRequest: ListRecordsRequest = {
      topicId,
      sort: "asc",
      order: "timestamp",
      limit: 1,
      ...this.handler.getRequestParams?.(),
    };

    const init = { signal: this.#controller.signal };

    Promise.all(
      Array.from(new Set(timestamps)).map(async (timestamp) => {
        const response = await this.listRecords(
          { ...baseRequest, timestamp },
          init,
        );

        return processFetchedRecords(this.handler, response);
      }),
    )
      .then((responses) => {
        const records = new Array<PlayerRecord<TRecordType>>();
        for (const response of responses) {
          for (const record of response.data) {
            records.push(record);
          }
        }

        if (records.length === 0) {
          throw new NoRecordsError();
        }

        this.#result = {
          status: "fulfilled",
          value: records,
        };
      })
      .catch((reason) => {
        this.#result = {
          status: "rejected",
          reason,
        };

        this.cancelScheduledFetches();
      })
      .finally(() => {
        this.notifySubscribers();
      });
  }

  protected onEvict(): void {
    this.#controller.abort();

    if (this.#result?.status !== "fulfilled") {
      return;
    }

    this.disposeOfRecords(this.#result.value);
  }
}

function createCacheEntry<TRecordType extends RecordType>(
  parameters: AnyCacheEntryParameters<TRecordType>,
  evictCacheEntry: (cacheKey: string) => void,
  listRecords: ListRecordsFn,
): AbstractCacheEntry<RecordsRequestType, TRecordType> {
  return match(parameters)
    .with(
      { type: "all" },
      (parameters) =>
        new AllRecordsCacheEntry({
          ...parameters,
          listRecords,
          evictCacheEntry,
        }),
    )
    .with(
      { type: "limited" },
      (parameters) =>
        new LimitedRecordsCacheEntry({
          ...parameters,
          listRecords,
          evictCacheEntry,
        }),
    )
    .with(
      { type: "exact" },
      (parameters) =>
        new ExactRecordsCacheEntry({
          ...parameters,
          listRecords,
          evictCacheEntry,
        }),
    )
    .exhaustive();
}

type AnyCacheEntryParameters<TRecordType extends RecordType> = StrictOmit<
  ConstructorParameters<
    | typeof AllRecordsCacheEntry<TRecordType>
    | typeof LimitedRecordsCacheEntry<TRecordType>
    | typeof ExactRecordsCacheEntry<TRecordType>
  >[0],
  "listRecords" | "evictCacheEntry"
>;

export class RecordStore {
  readonly #listRecords: ListRecordsFn;

  readonly #entries = new Map<string, AbstractCacheEntry<any, any>>();

  constructor(listRecords: ListRecordsFn) {
    this.#listRecords = listRecords;
  }

  getRecords<TRecordType extends RecordType>(
    request: RecordsRequest<TRecordType>,
  ): RecordsResponse<TRecordType> {
    const entry = this.#entries.get(createCacheKey(request));

    if (entry == null) {
      return { status: "pending" };
    } else {
      return entry.getRecords(request);
    }
  }

  subscribe(subscription: TopicSubscription<RecordType>): () => void {
    if (!checkIsUsableSubscription(subscription)) {
      // This is a no-op since no actual subscription is created.
      return () => {};
    }

    const entry = getOrInsertComputed(
      this.#entries,
      createCacheKey(subscription),
      () => createCacheEntry(subscription, this.#evictEntry, this.#listRecords),
    );

    return entry.subscribe(subscription);
  }

  clearError(identifier: CacheEntryIdentifier): void {
    const cacheKey = createCacheKey(identifier);

    const entry = this.#entries.get(cacheKey);

    invariant(entry != null, `No entry exists with cache key "${cacheKey}"`);

    entry.clearError();
  }

  seedCacheEntry<TRecordType extends RecordType>(
    parameters: MarkRequired<AnyCacheEntryParameters<TRecordType>, "records">,
    strict = true,
  ): void {
    const cacheKey = createCacheKey(parameters);

    const entryExists = this.#entries.has(cacheKey);

    if (!strict && entryExists) {
      return;
    }

    invariant(!entryExists, "Cache entry already exists");

    this.#entries.set(
      cacheKey,
      createCacheEntry(parameters, this.#evictEntry, this.#listRecords),
    );
  }

  #evictEntry = (cacheKey: string): void => {
    const entryExisted = this.#entries.delete(cacheKey);

    invariant(entryExisted, `No entry existed with cache key "${cacheKey}"`);
  };
}

function checkIsUsableSubscription<TRecordType extends RecordType>(
  subscription: TopicSubscription<TRecordType>,
): subscription is UsableSubscription<TRecordType> {
  return (
    checkIsExactRecordsSubscription(subscription) ||
    (subscription.topicStartTime != null && subscription.topicEndTime != null)
  );
}

function checkIsExactRecordsRequest<TRecordType extends RecordType>(
  request: RecordsRequest<TRecordType>,
): request is ExactRecordsRequest<TRecordType> {
  return request.type === "exact";
}

function checkIsExactRecordsSubscription<TRecordType extends RecordType>(
  subscription: TopicSubscription<TRecordType>,
): subscription is ExactRecordsTopicSubscription<TRecordType> {
  return checkIsExactRecordsRequest(subscription);
}

function removeArrayElement(array: Array<unknown>, element: unknown): void {
  const index = array.indexOf(element);

  if (index === -1) {
    return;
  }

  array.splice(index, 1);
}

/**
 * Given timestamp filters, convert them to the corresponding closed interval
 * as a time range.
 */
function toClosedInterval({
  timestampGt,
  timestampGte,
  timestampLt,
  timestampLte,
}: TimestampFilters): TimeRange {
  // Comparing time ranges is simpler if they're closed intervals. The exclusive
  // timestamp filters < and > can be represented using <= or >=, respectively,
  // by adding or subtracting 1 since timestamps belong to the natural numbers.
  // For example, `timestampGt: 3` is equivalent to `timestampGte: 4` since 4 is
  // 3's successor.

  // Prettier removes the parentheses since they're technically unnecessary but
  // that makes it less apparent what's happening.
  return {
    // prettier-ignore
    startTime: timestampGte ?? (timestampGt + 1n),
    // prettier-ignore
    endTime: timestampLte ?? (timestampLt - 1n),
  };
}

function checkDoIntervalsOverlap(first: TimeRange, second: TimeRange): boolean {
  return second.startTime <= first.endTime && first.startTime <= second.endTime;
}

function enforceResponseSortCorrectness(response: RecordListResponse) {
  // Can only check sort order if there's at least 2 records
  if (response.data.length <= 1) {
    return;
  }

  // Making the assumption that the backend returns all records in the same
  // sort order but that order may be the opposite of what was requested. Under
  // that assumption, it's sufficient to check the sort order of just the first
  // two records rather than spending time checking every record.
  let isSortedCorrectly: boolean;
  if (response.sort === "desc") {
    isSortedCorrectly = response.data[0].timestamp > response.data[1].timestamp;
  } else {
    isSortedCorrectly = response.data[0].timestamp < response.data[1].timestamp;
  }

  invariant(
    isSortedCorrectly,
    "Records do not appear to be sorted in correct order",
  );
}

/**
 * Given a subscription, calculates what the minimum cache count must be to
 * avoid cache thrashing or throwing away records that were immediately fetched.
 */
export function calculateMinimumCacheCount({
  count,
  prefetchBehind,
  prefetchAhead,
  limit,
}: Pick<
  LimitedRecordsTopicSubscription<RecordType>,
  "count" | "prefetchBehind" | "prefetchAhead" | "limit"
>): number {
  const maxFetchedDescendingCount =
    limit * Math.ceil((prefetchBehind + Math.ceil(count / 2)) / limit);

  const maxFetchedAscendingCount =
    limit * Math.ceil((Math.floor(count / 2) + prefetchAhead) / limit);

  return (
    2 * (Math.max(maxFetchedDescendingCount, maxFetchedAscendingCount) + limit)
  );
}

type Slice = readonly [start: number, end: number];

type EvictionStatus =
  | { action: "keep" }
  | { action: "remove" }
  | { action: "slice"; slices: ReadonlyArray<Slice> };

/**
 * Given a cached topic's record sequences and active subscriptions, calculate
 * whether each sequence should be kept in its entirety, removed in its
 * entirety or sliced into one or more sub-sequences.
 *
 * Conceptually, a window is calculated for each subscription and centered on
 * its timestamp in the same way as the records to fetch for a subscription are
 * calculated. This conceptual window is overlaid on the cached topic's record
 * sequences regardless of gaps between those sequences; the goal is to identify
 * the window of records closest to subscribers' timestamps and evict all other
 * records to reduce memory usage.
 */
export function calculateEvictionStatuses(
  sequences: ReadonlyArray<RecordSequence<RecordType>>,
  subscriptions: ReadonlySet<
    Pick<
      LimitedRecordsTopicSubscription<RecordType>,
      "timestamp" | "cacheCount"
    >
  >,
): ReadonlyArray<EvictionStatus> {
  const sequencesToSlices = new Map<RecordSequence<RecordType>, Array<Slice>>();

  for (const { timestamp, cacheCount } of subscriptions) {
    if (cacheCount == null) {
      return sequences.map(() => ({ action: "keep" }));
    }

    // The most recent sequence is the one which contains the timestamp or, if
    // no containing sequence exists, is the closest to the timestamp while
    // occurring before it.
    const mostRecentSequenceIndex = sequences.findIndex((sequence, i) => {
      if (sequence.canHandleTimestamp(timestamp)) {
        return true;
      }

      if (timestamp > sequence.endTime) {
        const nextSequence = sequences.at(i + 1);

        return nextSequence == null || timestamp < nextSequence.startTime;
      }

      return false;
    });

    const mostRecentRecordIndex =
      mostRecentSequenceIndex < 0
        ? -1
        : sequences[mostRecentSequenceIndex].getMostRecentRecordIndex(
            timestamp,
          );

    // Calculate how many records across all sequences are at or behind the
    // timestamp as well as ahead of it.
    let availableBehindCount = 0;
    let availableAheadCount = 0;
    for (
      let sequenceIndex = 0;
      sequenceIndex < sequences.length;
      sequenceIndex++
    ) {
      if (sequenceIndex < mostRecentSequenceIndex) {
        availableBehindCount += sequences[sequenceIndex].records.length;
      } else if (sequenceIndex > mostRecentSequenceIndex) {
        availableAheadCount += sequences[sequenceIndex].records.length;
      } else {
        // Even if `mostRecentRecordIndex` is -1 this will still produce the
        // correct result since there's 0 records available behind.
        const baseAvailableBehind = mostRecentRecordIndex + 1;

        availableBehindCount += baseAvailableBehind;
        availableAheadCount +=
          sequences[sequenceIndex].records.length - baseAvailableBehind;
      }
    }

    let cacheBehindCount = Math.ceil(cacheCount / 2);
    let cacheAheadCount = Math.floor(cacheCount / 2);

    // To preserve as many records as possible (up to cache count), determine if
    // there are less records in a direction than requested. If there are, shift
    // the leftover count to the opposite direction to preserve more of its
    // records if possible.
    if (cacheBehindCount > availableBehindCount) {
      const leftoverCount = cacheBehindCount - availableBehindCount;

      cacheBehindCount -= leftoverCount;
      cacheAheadCount += leftoverCount;
    } else if (cacheAheadCount > availableAheadCount) {
      const leftoverCount = cacheAheadCount - availableAheadCount;

      cacheBehindCount += leftoverCount;
      cacheAheadCount -= leftoverCount;
    }

    // Calculate what slices of each sequence up to and including the most
    // recent sequence should be preserved.
    for (
      let sequenceIndex = mostRecentSequenceIndex;
      // If `mostRecentSequenceIndex` was -1 this will keep the loop from ever
      // running. If it was -1 there shouldn't be any records to cache behind
      // anyways.
      sequenceIndex >= 0 && cacheBehindCount > 0;
      sequenceIndex--
    ) {
      const currentSequence = sequences[sequenceIndex];

      const end =
        sequenceIndex === mostRecentSequenceIndex
          ? mostRecentRecordIndex + 1
          : currentSequence.records.length;
      const start = Math.max(end - cacheBehindCount, 0);

      getOrInsert(sequencesToSlices, currentSequence, []).push([start, end]);

      cacheBehindCount -= end - start;
    }

    // Calculate what slices of each sequence from the most recent sequence to
    // the end of available sequences should be preserved.
    for (
      let sequenceIndex = Math.max(mostRecentSequenceIndex, 0);
      sequenceIndex < sequences.length && cacheAheadCount > 0;
      sequenceIndex++
    ) {
      const currentSequence = sequences[sequenceIndex];

      const start =
        sequenceIndex === mostRecentSequenceIndex
          ? mostRecentRecordIndex + 1
          : 0;
      const end = Math.min(
        currentSequence.records.length,
        start + cacheAheadCount,
      );

      getOrInsert(sequencesToSlices, currentSequence, []).push([start, end]);

      cacheAheadCount -= end - start;
    }
  }

  return sequences.map((sequence): EvictionStatus => {
    const slices = sequencesToSlices.get(sequence);

    if (slices == null) {
      return { action: "remove" };
    } else {
      const mergedSlices = new Array<Slice>();

      for (let i = 0; i < slices.length; i++) {
        let baseSlice = slices[i];

        for (let j = i + 1; j < slices.length; ) {
          const comparisonSlice = slices[j];

          if (checkDoSlicesOverlap(baseSlice, comparisonSlice)) {
            slices.splice(j, 1);

            baseSlice = [
              Math.min(baseSlice[0], comparisonSlice[0]),
              Math.max(baseSlice[1], comparisonSlice[1]),
            ];

            // It's possible a previous slice was skipped because it couldn't
            // merge with the original base slice but could merge with the
            // updated base slice, so start the loop over.
            j = i + 1;
          } else {
            j++;
          }
        }

        mergedSlices.push(baseSlice);
      }

      if (mergedSlices.length === 1) {
        const [[start, end]] = mergedSlices;
        const sliceLength = end - start;

        if (sliceLength === 0) {
          // This case may not actually be possible but it should still be
          // handled just for in case so later code can assume all slices will
          // produce non-empty results.
          return { action: "remove" };
        } else if (sliceLength === sequence.records.length) {
          return { action: "keep" };
        } else {
          return { action: "slice", slices: mergedSlices };
        }
      } else {
        return { action: "slice", slices: mergedSlices };
      }
    }
  });
}

function checkDoSlicesOverlap(first: Slice, second: Slice): boolean {
  return second[0] <= first[1] && first[0] <= second[1];
}

function normalizeDataFilers(
  filters: DataFilter | ReadonlyArray<DataFilter> | undefined,
): ReadonlyArray<DataFilter> {
  if (filters == null) {
    return [];
  } else if (Array.isArray(filters)) {
    return filters;
  } else {
    return [filters as DataFilter];
  }
}

function createCacheKey({
  type,
  recordType,
  topicId,
  frequency = null,
  queryDataFilter,
  timestamps = [],
}: CacheEntryIdentifier): string {
  // `hashQueryKey` is used here because it produces a stable stringified
  // value regardless of object key ordering.
  // Also, it's important we're only creating the cache key using fields
  // explicitly defined in `CacheEntryIdentifier`, so we need to destructure
  // the parameter and reconstruct it to hash.
  return hashQueryKey([
    {
      type,
      recordType,
      topicId,
      frequency,
      queryDataFilter: normalizeDataFilers(queryDataFilter),
      timestamps: timestamps.map(String),
    },
  ]);
}

function comparePlayerRecords(
  a: PlayerRecord<RecordType>,
  b: PlayerRecord<RecordType>,
): number {
  return compareBigInts(a.timestamp, b.timestamp);
}

function floorTimestampForFrequency(
  timestamp: bigint,
  frequency: SampleFrequencyValue | null,
): bigint {
  if (frequency == null) {
    return timestamp;
  }

  const divisor = {
    [SampleFrequency.Second]: secondsToNanoseconds(1),
    [SampleFrequency.HalfSecond]: millisecondsToNanoseconds(500),
    [SampleFrequency.Decisecond]: millisecondsToNanoseconds(100),
  }[frequency];

  // We want to floor the timestamp to the most recent multiple of the
  // period. Since we're dealing with bigints here which implicitly do floor
  // division, dividing by the multiple which the period represents and
  // multiplying again gets us to the correct value.
  return (timestamp / divisor) * divisor;
}

function calculatePeriodForFrequency(frequency: number | null): bigint {
  if (frequency == null) {
    return 1n;
  } else {
    return secondsToNanoseconds(1) / BigInt(frequency);
  }
}

/**
 * Given some timestamp and frequency, calculate the inclusive start time of
 * the bin to which the timestamp belongs based on the frequency. The start time
 * is a multiple of the period calculated using the frequency
 */
function calculateStartOfBin(
  timestamp: bigint,
  frequency: number | null,
): bigint {
  const period = calculatePeriodForFrequency(frequency);

  return (timestamp / period) * period;
}

/**
 * Given some timestamp and frequency, calculate the inclusive start time of the
 * bin after the bin to which the timestamp belongs based on the frequency.
 */
function calculateStartOfNextBin(
  timestamp: bigint,
  frequency: number | null,
): bigint {
  const period = calculatePeriodForFrequency(frequency);

  const startOfBin = calculateStartOfBin(timestamp, frequency);

  return startOfBin + period;
}

interface BoundingSequences<TRecordType extends RecordType> {
  pastSequence: Maybe<RecordSequence<TRecordType>>;
  containingSequence: Maybe<RecordSequence<TRecordType>>;
  futureSequence: Maybe<RecordSequence<TRecordType>>;
}

function findBoundingSequences<TRecordType extends RecordType>({
  frequency,
  timestamp,
  sequences,
}: {
  frequency: number | null;
  timestamp: bigint;
  sequences: ReadonlyArray<RecordSequence<TRecordType>>;
}): BoundingSequences<TRecordType> {
  const containingSequence = sequences.find((sequence) => {
    if (sequence.hasFirstTopicRecord && timestamp < sequence.startTime) {
      return true;
    } else if (sequence.hasLastTopicRecord && timestamp > sequence.endTime) {
      return true;
    } else {
      return (
        calculateStartOfBin(sequence.startTime, frequency) <= timestamp &&
        timestamp < calculateStartOfNextBin(sequence.endTime, frequency)
      );
    }
  });

  const pastSequence = sequences.findLast(
    (sequence) =>
      sequence.endTime < (containingSequence?.startTime ?? timestamp),
  );

  const futureSequence = sequences.find(
    (sequence) =>
      (containingSequence?.endTime ?? timestamp) < sequence.startTime,
  );

  return {
    pastSequence,
    containingSequence,
    futureSequence,
  };
}

type CalculateTimestampFiltersParams = Pick<
  LimitedRecordsRequest<any>,
  "frequency" | "timestamp"
> &
  BoundingSequences<any> &
  Pick<LimitedRecordsCacheEntry<any>, "topicStartTime" | "topicEndTime">;

/**
 * Calculate the timestamp filters in which records could be fetched around
 * the given timestamp using the given frequency without overlapping existing
 * record sequences.
 *
 * If `pastSequence` exists, it acts as the lower bound on any record fetching;
 * otherwise, the topic's start time acts as the lower bound.
 *
 * If `futureSequence` exists, it acts as the upper bound on any record
 * fetching; otherwise, the topic's end time acts as the lower bound.
 *
 * If `containingSequence` doesn't exist, the descending and ascending filters
 * will produce continuous, non-overlapping ranges; if it does exist, the
 * descending and ascending filters will be non-continuous with the sequence
 * acting as the upper bound for the descending filters and the lower bound
 * for the ascending filters.
 */
function calculateTimestampFilters({
  frequency,
  timestamp,
  pastSequence,
  containingSequence,
  futureSequence,
  topicStartTime,
  topicEndTime,
}: CalculateTimestampFiltersParams): {
  descendingFilters: TimestampFilters;
  ascendingFilters: TimestampFilters;
  alignedWithBin: boolean;
} {
  const alignWithBin =
    containingSequence == null &&
    timestamp !== calculateStartOfNextBin(timestamp, frequency) - 1n;

  let descLowerBound;
  if (pastSequence != null) {
    descLowerBound = {
      timestampGte: calculateStartOfNextBin(pastSequence.endTime, frequency),
    };
  } else {
    descLowerBound = {
      timestampGte: topicStartTime,
    };
  }

  let descUpperBound;
  if (containingSequence != null) {
    descUpperBound = {
      timestampLt: calculateStartOfBin(containingSequence.startTime, frequency),
    };
  } else if (alignWithBin) {
    descUpperBound = {
      timestampLt: calculateStartOfBin(timestamp, frequency),
    };
  } else {
    descUpperBound = {
      timestampLte: timestamp,
    };
  }

  let ascLowerBound;
  if (containingSequence != null) {
    ascLowerBound = {
      timestampGte: calculateStartOfNextBin(
        containingSequence.endTime,
        frequency,
      ),
    };
  } else if (alignWithBin) {
    ascLowerBound = {
      timestampGte: calculateStartOfBin(timestamp, frequency),
    };
  } else {
    ascLowerBound = {
      timestampGt: timestamp,
    };
  }

  let ascUpperBound;
  if (futureSequence != null) {
    ascUpperBound = {
      timestampLt: calculateStartOfBin(futureSequence.startTime, frequency),
    };
  } else {
    ascUpperBound = {
      timestampLte: topicEndTime,
    };
  }

  return {
    descendingFilters: {
      ...descLowerBound,
      ...descUpperBound,
    },
    ascendingFilters: {
      ...ascLowerBound,
      ...ascUpperBound,
    },
    alignedWithBin: alignWithBin,
  };
}

type CalculateDirectionCountsParams = Pick<
  LimitedRecordsTopicSubscription<any>,
  "count" | "timestamp" | "prefetchBehind" | "prefetchAhead"
> &
  Pick<LimitedRecordsCacheEntry<any>, "topicStartTime" | "topicEndTime"> &
  Pick<BoundingSequences<any>, "containingSequence">;

/**
 * Calculate how many records need to be fetched in descending and ascending
 * directions relative to the given timestamp.
 */
function calculateDirectionalCounts({
  count,
  timestamp,
  prefetchBehind,
  prefetchAhead,
  topicStartTime,
  topicEndTime,
  containingSequence,
}: CalculateDirectionCountsParams): {
  descendingCount: number;
  ascendingCount: number;
} {
  let descendingCount = Math.ceil(count / 2) + prefetchBehind;
  let ascendingCount = Math.floor(count / 2) + prefetchAhead;

  if (containingSequence != null) {
    const remainingRecords = containingSequence.calculateRemainingRecords({
      timestamp,
      countBehind: descendingCount,
      countAhead: ascendingCount,
    });

    descendingCount = remainingRecords.behind;
    ascendingCount = remainingRecords.ahead;
  } else if (timestamp < topicStartTime) {
    ascendingCount += descendingCount;
    descendingCount = 0;
  } else if (timestamp >= topicEndTime) {
    descendingCount += ascendingCount;
    ascendingCount = 0;
  }

  return {
    descendingCount,
    ascendingCount,
  };
}

function checkAreFiltersValid(filters: TimestampFilters): boolean {
  const interval = toClosedInterval(filters);

  // Filters which span 0 nanoseconds are still valid because that means both
  // the start and end times are equal (after normalization), which could return
  // a single record whose timestamp matches those bounds.
  return interval.endTime - interval.startTime >= 0;
}

/**
 * Determines if there are no in-flight requests whose records could overlap
 * with any fetched by the given timestamp filters.
 *
 * In the simplest case, the filters overlap with a request if the request's
 * normalized range overlaps with the filters'. Beyond this simple case, there
 * are several other situations where the timestamp filters effectively overlap
 * with an in-flight request:
 *
 * 1. When the request's tracker has its `previousSequenceEndTime` or
 *    `nextSequenceStartTime` set and the filters overlap with the gap between
 *    the tracker's time range and the sequence it'll merge with once the
 *    request finishes.
 * 2. When the filters are before a tracker where there are no prior records or
 *    after a tracker where there are no subsequent records. This indicates the
 *    used topic bounds will eventually be updated and so there shouldn't be any
 *    new requests made before or after the tracker's covered range.
 */
function checkNoOverlappingRequests(
  filters: TimestampFilters,
  trackers: ReadonlyArray<RequestTracker<any>>,
): boolean {
  const filtersRange = toClosedInterval(filters);

  for (const tracker of trackers) {
    const trackerRange = tracker.getTimeRange();

    let { startTime, endTime } = trackerRange;

    if (tracker.previousSequenceEndTime != null) {
      startTime = tracker.previousSequenceEndTime + 1n;
    }

    if (tracker.nextSequenceStartTime != null) {
      endTime = tracker.nextSequenceStartTime - 1n;
    }

    if (checkDoIntervalsOverlap(filtersRange, { startTime, endTime })) {
      return false;
    } else if (
      tracker.noPriorRecords &&
      filtersRange.endTime < trackerRange.startTime
    ) {
      return false;
    } else if (
      tracker.noSubsequentRecords &&
      trackerRange.endTime < filtersRange.startTime
    ) {
      return false;
    }
  }

  return true;
}
